import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { startOfMonth, endOfMonth, addMonths, format, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  CircularProgress,
  Snackbar,
  TextField,
  MenuItem,
  InputAdornment,
  Tabs,
  Tab,
  Autocomplete,
  FormControlLabel,
  Switch,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HistoryIcon from '@mui/icons-material/History';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { supabase } from '../lib/supabase';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// Função para formatar data sem conversão de timezone
export const formatDateWithoutTimezone = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

function MetricCard({ title, value, change, icon, type }) {
  return (
    <Paper sx={{ 
      p: 2.5, 
      height: '100%',
      backgroundColor: 'white',
      borderLeft: `4px solid ${type === 'positive' ? 'success.main' : type === 'negative' ? 'error.main' : 'primary.main'}`,
      transition: 'transform 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
      }
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary' }}>
            {title}
          </Typography>
          <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {type === 'currency' ? formatCurrency(value) : value}
          </Typography>
        </Box>
        <Box
          sx={{
            p: 1,
            borderRadius: 2,
            backgroundColor: type === 'positive' ? 'success.main' : type === 'negative' ? 'error.main' : 'primary.main',
            color: 'white',
          }}
        >
          {icon === 'balance' && <AccountBalanceWalletIcon />}
          {icon === 'payments' && <TrendingUpIcon />}
          {icon === 'receipt' && <AttachMoneyIcon />}
          {icon === 'wallet' && <TrendingDownIcon />}
          {icon === 'account_balance' && <AccountBalanceWalletIcon />}
          {icon === 'show_chart' && <TrendingUpIcon />}
          {typeof icon === 'object' && icon}
        </Box>
      </Box>
    </Paper>
  );
}

function TransactionList({ transactions, loading, onDelete, onEdit, onViewHistory }) {
  const [transactionsWithHistory, setTransactionsWithHistory] = useState({});
  const [parentCategories, setParentCategories] = useState({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    // Buscar todas as categorias para mapear as categorias pai
    const fetchParentCategories = async () => {
      try {
        const { data: categories } = await supabase
          .from('personal_categories')
          .select('id, name, parent_id')
          .eq('type', 'saida');
        
        if (categories) {
          const parentMap = {};
          categories.forEach(category => {
            parentMap[category.id] = category;
          });
          setParentCategories(parentMap);
        }
      } catch (error) {
        console.error('Error fetching parent categories:', error);
      }
    };

    fetchParentCategories();
  }, []);

  useEffect(() => {
    const checkTransactionsHistory = async () => {
      const historyChecks = await Promise.all(
        transactions.map(async (transaction) => {
          const { data } = await supabase
            .from('transaction_history')
            .select('id')
            .eq('transaction_id', transaction.id)
            .limit(1);
          
          return { 
            id: transaction.id, 
            hasHistory: data && data.length > 0 
          };
        })
      );

        const historyMap = {};
        historyChecks.forEach(check => {
          historyMap[check.id] = check.hasHistory;
        });
        setTransactionsWithHistory(historyMap);
      };

      if (transactions.length > 0) {
        checkTransactionsHistory();
      }
    }, [transactions]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!transactions.length) {
    return (
      <Box sx={{ textAlign: 'center', p: 3 }}>
        <Typography color="text.secondary">
          Nenhuma transação encontrada
        </Typography>
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Box>
        {transactions.map((transaction) => (
          <Paper key={transaction.id} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {transaction.description}
              </Typography>
              <Typography
                variant="subtitle2"
                color={transaction.type === 'entrada' ? 'success.main' : 'error.main'}
                sx={{ fontWeight: 600 }}
              >
                {formatCurrency(Math.abs(transaction.value))}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {formatDateWithoutTimezone(transaction.date)}
              </Typography>
              <Chip 
                label={
                  transaction.category?.parent_id && parentCategories[transaction.category.parent_id]?.name
                    ? `${parentCategories[transaction.category.parent_id].name} > ${transaction.category.name}`
                    : transaction.category?.name || 
                      (transaction.type === 'entrada' ? 'Entrada' : 'Sem categoria')
                } 
                size="small"
                variant="outlined"
              />
            </Box>

            {transaction.payment_source?.name && (
              <Box sx={{ mb: 1 }}>
                <Chip 
                  label={transaction.payment_source.name}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
              {transactionsWithHistory[transaction.id] && (
                <IconButton 
                  size="small" 
                  onClick={() => onViewHistory(transaction)}
                  title="Ver histórico"
                  sx={{ 
                    backgroundColor: 'background.subtle',
                    '&:hover': { backgroundColor: 'grey.200' }
                  }}
                >
                  <HistoryIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton 
                size="small" 
                onClick={() => onEdit(transaction)}
                title="Editar"
                sx={{ 
                  backgroundColor: 'background.subtle',
                  '&:hover': { backgroundColor: 'grey.200' }
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={() => onDelete(transaction)}
                title="Deletar"
                color="error"
                sx={{ 
                  backgroundColor: 'error.light',
                  color: 'white',
                  '&:hover': { backgroundColor: 'error.main' }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        ))}
        
        <Paper sx={{ p: 2, mt: 2, backgroundColor: 'background.subtle' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Total:
            </Typography>
            <Typography
              variant="subtitle1"
              color={
                transactions.reduce((sum, t) => sum + parseFloat(t.value), 0) >= 0
                  ? 'success.main'
                  : 'error.main'
              }
              sx={{ fontWeight: 600 }}
            >
              {formatCurrency(
                transactions.reduce((sum, t) => sum + parseFloat(t.value), 0)
              )}
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <TableContainer sx={{ width: '100%' }}>
      <Table sx={{ width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell>Data</TableCell>
            <TableCell>Descrição</TableCell>
            <TableCell>Categoria</TableCell>
            <TableCell>Origem</TableCell>
            <TableCell align="right">Valor</TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions.map((transaction) => (
            <TableRow key={transaction.id}>
              <TableCell>
                {formatDateWithoutTimezone(transaction.date)}
              </TableCell>
              <TableCell>
                {transaction.description}
              </TableCell>
              <TableCell>
                <Chip 
                  label={
                    transaction.category?.parent_id && parentCategories[transaction.category.parent_id]?.name
                      ? `${parentCategories[transaction.category.parent_id].name} > ${transaction.category.name}`
                      : transaction.category?.name || 
                        (transaction.type === 'entrada' ? 'Entrada' : 'Sem categoria')
                  } 
                  size="small"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                {transaction.payment_source?.name && (
                  <Chip 
                    label={transaction.payment_source.name}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </TableCell>
              <TableCell align="right">
                <Typography
                  color={transaction.type === 'entrada' ? 'success.main' : 'error.main'}
                >
                  {formatCurrency(Math.abs(transaction.value))}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                  {transactionsWithHistory[transaction.id] && (
                    <IconButton 
                      size="small" 
                      onClick={() => onViewHistory(transaction)}
                      title="Ver histórico"
                      sx={{ 
                        minWidth: '44px',
                        minHeight: '44px'
                      }}
                    >
                      <HistoryIcon />
                    </IconButton>
                  )}
                  <IconButton 
                    size="small" 
                    onClick={() => onEdit(transaction)}
                    title="Editar"
                    sx={{ 
                      minWidth: '44px',
                      minHeight: '44px'
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => onDelete(transaction)}
                    title="Deletar"
                    color="error"
                    sx={{ 
                      minWidth: '44px',
                      minHeight: '44px'
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={4} align="right">
              <Typography variant="subtitle1">Total:</Typography>
            </TableCell>
            <TableCell align="right" colSpan={2}>
              <Typography
                variant="subtitle1"
                color={
                  transactions.reduce((sum, t) => sum + parseFloat(t.value), 0) >= 0
                    ? 'success.main'
                    : 'error.main'
                }
              >
                {formatCurrency(
                  transactions.reduce((sum, t) => sum + parseFloat(t.value), 0)
                )}
              </Typography>
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}

const TransactionForm = ({ onTransactionAdded, editingTransaction }) => {
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    type: 'saida',
    category: '',
    payment_source: '',
    date: new Date()
  });
  const [categories, setCategories] = useState([]);
  const [paymentSources, setPaymentSources] = useState([]);
  const [expenseHistory, setExpenseHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleValueChange = (e) => {
    const value = e.target.value;
    // Remove non-numeric characters
    const numericValue = value.replace(/\D/g, '');
    // Format as Brazilian currency
    const formattedValue = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue / 100);

    setFormData(prev => ({
      ...prev,
      amount: formattedValue
    }));
  };

  useEffect(() => {
    fetchCategories();
    fetchPaymentSources();
    fetchExpenseHistory();
    if (editingTransaction) {
      setFormData({
        description: editingTransaction.description,
        amount: formatCurrency(Math.abs(editingTransaction.value)),
        type: editingTransaction.type,
        category: editingTransaction.category_id,
        payment_source: editingTransaction.payment_source_id || '',
        date: new Date(editingTransaction.date)
      });
    }
  }, [editingTransaction]);

  const fetchPaymentSources = async () => {
    try {
      const { data } = await supabase
        .from('payment_sources')
        .select('*')
        .eq('is_active', true)
        .order('name');
      
      setPaymentSources(data || []);
    } catch (error) {
      console.error('Error fetching payment sources:', error);
      setError('Erro ao carregar origens de pagamento');
    }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await supabase
        .from('personal_categories')
        .select(`
          *,
          parent:parent_id (id, name)
        `)
        .eq('type', 'saida')
        .order('name');
      
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Erro ao carregar categorias');
    }
  };


  const fetchExpenseHistory = async () => {
    try {
      const { data } = await supabase
        .from('personal_transactions')
        .select(`
          description,
          value,
          category_id,
          category:personal_categories(name)
        `)
        .eq('type', 'saida')
        .order('date', { ascending: false })
        .limit(50);
      
      // Agrupa por descrição e pega o mais recente de cada
      const uniqueExpenses = data.reduce((acc, curr) => {
        if (!acc[curr.description]) {
          acc[curr.description] = curr;
        }
        return acc;
      }, {});

      setExpenseHistory(Object.values(uniqueExpenses));
    } catch (error) {
      console.error('Error fetching expense history:', error);
      setError('Erro ao carregar histórico de despesas');
    }
  };


  const handleExpenseSelect = (expense) => {
    setFormData(prev => ({
      ...prev,
      description: expense.description,
      amount: Math.abs(expense.value).toString(),
      category: expense.category_id
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (newDate) => {
    setFormData(prev => ({
      ...prev,
      date: newDate
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const transactionData = {
        description: formData.description.trim(),
        value: formData.type === 'saida' 
          ? -(parseFloat(formData.amount.replace(/[R$\s.]/g, '').replace(',', '.')) || 0)
          : (parseFloat(formData.amount.replace(/[R$\s.]/g, '').replace(',', '.')) || 0),
        type: formData.type,
        category_id: formData.type === 'saida' ? formData.category : null,
        payment_source_id: formData.payment_source || null,
        date: formData.date.toISOString().split('T')[0],
      };

      if (editingTransaction) {
        const { error } = await supabase
          .from('personal_transactions')
          .update([transactionData])
          .eq('id', editingTransaction.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('personal_transactions')
          .insert([transactionData]);

        if (error) throw error;
      }

      // Limpar o formulário
      setFormData({
        description: '',
        amount: '',
        type: 'saida',
        category: '',
        date: new Date()
      });

      // Atualizar o histórico de despesas
      if (formData.type === 'saida') {
        fetchExpenseHistory();
      }

      // Notificar o componente pai
      onTransactionAdded(transactionData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            select
            label="Tipo"
            name="type"
            value={formData.type}
            onChange={handleChange}
          >
            <MenuItem value="entrada">Entrada</MenuItem>
            <MenuItem value="saida">Saída</MenuItem>
          </TextField>
        </Grid>

        {formData.type === 'saida' ? (
          <>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={expenseHistory}
                getOptionLabel={(option) => 
                  typeof option === 'string' ? option : option.description
                }
                inputValue={formData.description}
                onInputChange={(event, newValue) => {
                  setFormData(prev => ({
                    ...prev,
                    description: newValue || ''
                  }));
                }}
                onChange={(event, newValue) => {
                  if (newValue && typeof newValue === 'object') {
                    handleExpenseSelect(newValue);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Descrição"
                    required
                    fullWidth
                    name="description"
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography>{option.description}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.category?.name} - {formatCurrency(Math.abs(option.value))}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Categoria"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                {categories
                  .filter(cat => !cat.parent_id) // Apenas categorias principais
                  .map((category) => [
                    // Categoria principal
                    <MenuItem key={category.id} value={category.id}>
                      <strong>{category.name}</strong>
                    </MenuItem>,
                    // Subcategorias desta categoria
                    ...categories
                      .filter(subcat => subcat.parent_id === category.id)
                      .map(subcat => (
                        <MenuItem key={subcat.id} value={subcat.id} sx={{ pl: 3 }}>
                          ↳ {subcat.name}
                        </MenuItem>
                      ))
                  ])
                  .flat()}
              </TextField>
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Descrição"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Valor"
            name="amount"
            value={formData.amount}
            onChange={handleValueChange}
            InputProps={{
              inputProps: {
                inputMode: 'numeric'
              }
            }}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <DatePicker
              label="Data"
              value={formData.date}
              onChange={handleDateChange}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </LocalizationProvider>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            select
            label="Origem do Pagamento"
            name="payment_source"
            value={formData.payment_source}
            onChange={handleChange}
          >
            <MenuItem value="">
              <em>Selecione a origem</em>
            </MenuItem>
            {paymentSources.map((source) => (
              <MenuItem key={source.id} value={source.id}>
                {source.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Salvando...' : editingTransaction ? 'Atualizar Transação' : 'Salvar Transação'}
          </Button>
          {error && (
            <Typography color="error" sx={{ mt: 1 }}>
              Erro: {error}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

function CashFlow() {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [currentTab, setCurrentTab] = useState('all');
  const [showFixedExpensesOnly, setShowFixedExpensesOnly] = useState(false);
  const [selectedCategory] = useState('');
  const [openNewTransaction, setOpenNewTransaction] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [selectedTransactionHistory, setSelectedTransactionHistory] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedMonth, setSelectedMonth] = useState(null);
  const location = useLocation();

  const calculateTrend = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / Math.abs(previous)) * 100;
  };

  const calculateMetrics = (transactions) => {
    const metrics = transactions.reduce((acc, transaction) => {
      const value = parseFloat(transaction.value);
      if (transaction.type === 'entrada') {
        acc.totalIncome += value;
      } else {
        acc.totalExpenses += Math.abs(value);
      }
      return acc;
    }, { totalIncome: 0, totalExpenses: 0 });

    return {
      ...metrics,
      balance: metrics.totalIncome - metrics.totalExpenses
    };
  };

  useEffect(() => {
    fetchTransactions();

    // Listen for parcelamento deletion to refresh transactions
    const handlePersonalInstallmentDeleted = () => {
      console.log('Evento personalInstallmentDeleted recebido, atualizando transações...');
      fetchTransactions();
    };
    window.addEventListener('personalInstallmentDeleted', handlePersonalInstallmentDeleted);
    
    // Set up real-time subscription for personal_transactions
    const subscription = supabase
      .channel('personal-transactions-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'personal_transactions'
        },
        (payload) => {
          console.log('Mudança detectada na tabela personal_transactions:', payload.eventType);
          fetchTransactions();
        }
      )
      .subscribe();
    
    return () => {
      window.removeEventListener('personalInstallmentDeleted', handlePersonalInstallmentDeleted);
      subscription.unsubscribe();
    };
  }, []);

  // Aplicar filtro automático do mês vigente se especificado
  useEffect(() => {
    if (location.state?.autoFilterCurrentMonth) {
      const today = new Date();
      const firstDayOfMonth = startOfMonth(today);
      const lastDayOfMonth = endOfMonth(today);
      setDateRange([firstDayOfMonth, lastDayOfMonth]);
    }
  }, [location.state]);

  useEffect(() => {
    if (transactions.length > 0) {
      let filtered = [...transactions];

      // Filtrar por texto
      if (searchTerm) {
        filtered = filtered.filter(transaction =>
          transaction.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          transaction.category?.name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Filtrar por tipo de transação
      if (currentTab === 'income') {
        filtered = filtered.filter(t => t.type === 'entrada');
      } else if (currentTab === 'expense') {
        filtered = filtered.filter(t => t.type === 'saida');
      }

      // Filtrar por status (ativo/arquivado)
      if (currentTab === 'active') {
        filtered = filtered.filter(t => !t.deleted_at);
      } else if (currentTab === 'archived') {
        filtered = filtered.filter(t => t.deleted_at);
      }

      // Filtrar por categoria
      if (selectedCategory) {
        filtered = filtered.filter(transaction => 
          transaction.category_id === selectedCategory
        );
      }

      // Filtrar por data
      if (dateRange[0] && dateRange[1]) {
        filtered = filtered.filter(transaction => {
          const transactionDate = new Date(transaction.date);
          return transactionDate >= dateRange[0] && 
                 transactionDate <= dateRange[1];
        });
      }

      // Filtrar apenas despesas fixas se o filtro estiver ativo
      if (showFixedExpensesOnly) {
        filtered = filtered.filter(transaction => 
          transaction.type === 'saida' && 
          transaction.category?.expense_type === 'fixed'
        );
      }

      setFilteredTransactions(filtered);
    }
  }, [transactions, searchTerm, currentTab, showFixedExpensesOnly, dateRange, selectedCategory]);

  const handleTransactionAdded = (transaction) => {
    fetchTransactions();
    setOpenNewTransaction(false);
  };

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      // Fetch all transactions with their categories and payment sources
      const { data: transactions, error } = await supabase
        .from('personal_transactions')
        .select(`
          *,
          category:personal_categories(name, expense_type, parent_id),
          payment_source:payment_sources(name)
        `)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Removed console logs to prevent infinite loop spam

      // Update state with all transactions
      setTransactions(transactions);
      setFilteredTransactions(transactions);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (transaction) => {
    try {
      const { error } = await supabase
        .from('personal_transactions')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', transaction.id);

      if (error) throw error;

      setSnackbar({
        open: true,
        message: 'Transação deletada com sucesso'
      });

      fetchTransactions();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao deletar a transação'
      });
    }
  };

  const handleEditTransaction = async (transaction) => {
    setEditingTransaction(transaction);
    setOpenNewTransaction(true);
  };

  const handleViewHistory = async (transaction) => {
    try {
      setLoading(true);
      const { data: history, error } = await supabase
        .from('transaction_history')
        .select(`
          *,
          user:users(email)
        `)
        .eq('transaction_id', transaction.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setSelectedTransactionHistory({
        transaction,
        history: history || []
      });
      setOpenHistoryDialog(true);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar histórico',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Função para gerar os botões de meses
  const generateMonthButtons = () => {
    const today = new Date();
    const months = [];
    
    // Gerar 6 meses (atual + 5 anteriores)
    for (let i = 0; i < 6; i++) {
      const monthDate = subMonths(today, i);
      const monthName = format(monthDate, 'MMMM', { locale: ptBR });
      const monthYear = format(monthDate, 'yyyy');
      const monthKey = format(monthDate, 'yyyy-MM');
      
      months.push({
        key: monthKey,
        name: monthName.charAt(0).toUpperCase() + monthName.slice(1),
        year: monthYear,
        date: monthDate
      });
    }
    
    return months;
  };

  // Função para filtrar por mês
  const handleMonthFilter = (monthDate) => {
    const firstDay = startOfMonth(monthDate);
    const lastDay = endOfMonth(monthDate);
    setDateRange([firstDay, lastDay]);
    setSelectedMonth(monthDate);
  };

  // Função para limpar filtro de mês
  const clearMonthFilter = () => {
    setDateRange([null, null]);
    setSelectedMonth(null);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        {/* Métricas */}
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Saldo"
            value={formatCurrency(calculateMetrics(filteredTransactions).balance)}
            icon={<AccountBalanceWalletIcon />}
            type={calculateMetrics(filteredTransactions).balance >= 0 ? 'positive' : 'negative'}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Entradas"
            value={calculateMetrics(filteredTransactions).totalIncome}
            change={calculateTrend(calculateMetrics(filteredTransactions).totalIncome, 0)}
            icon="payments"
            type="currency"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard
            title="Saídas"
            value={calculateMetrics(filteredTransactions).totalExpenses}
            change={calculateTrend(calculateMetrics(filteredTransactions).totalExpenses, 0)}
            icon="wallet"
            type="currency"
          />
        </Grid>

        {/* Botões de Meses */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" sx={{ mr: 2, color: 'text.secondary' }}>
                Filtrar por mês:
              </Typography>
              {generateMonthButtons().map((month) => (
                <Button
                  key={month.key}
                  variant={selectedMonth && format(selectedMonth, 'yyyy-MM') === month.key ? "contained" : "outlined"}
                  size="small"
                  onClick={() => handleMonthFilter(month.date)}
                  sx={{ 
                    minWidth: 'auto',
                    px: 2,
                    backgroundColor: selectedMonth && format(selectedMonth, 'yyyy-MM') === month.key ? 'primary.main' : 'transparent',
                    '&:hover': {
                      backgroundColor: selectedMonth && format(selectedMonth, 'yyyy-MM') === month.key ? 'primary.dark' : 'grey.100'
                    }
                  }}
                >
                  {month.name}
                </Button>
              ))}
              {selectedMonth && (
                <Button
                  variant="text"
                  size="small"
                  onClick={clearMonthFilter}
                  sx={{ 
                    color: 'text.secondary',
                    ml: 'auto',
                    '&:hover': {
                      color: 'primary.main'
                    }
                  }}
                >
                  Limpar filtro
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Filtros */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Buscar"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                  <DatePicker
                    label="Data Inicial"
                    value={dateRange[0]}
                    onChange={(newValue) => setDateRange([newValue, dateRange[1]])}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                  <DatePicker
                    label="Data Final"
                    value={dateRange[1]}
                    onChange={(newValue) => setDateRange([dateRange[0], newValue])}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    setEditingTransaction(null);
                    setOpenNewTransaction(true);
                  }}
                  startIcon={<AddIcon />}
                >
                  Nova Transação
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2 }}>
              <Tabs
                value={currentTab}
                onChange={(e, newValue) => setCurrentTab(newValue)}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab value="all" label="Todas" />
                <Tab value="income" label="Entradas" />
                <Tab value="expense" label="Saídas" />
              </Tabs>
            </Box>
          </Paper>
        </Grid>

        {/* Lista de Transações */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  setEditingTransaction(null);
                  setOpenNewTransaction(true);
                }}
                startIcon={<AddIcon />}
              >
                Nova Transação
              </Button>
              <Button
                variant="outlined"
                onClick={fetchTransactions}
                title="Recarregar transações"
              >
                Atualizar
              </Button>
              {currentTab === 'expense' && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={showFixedExpensesOnly}
                      onChange={(e) => setShowFixedExpensesOnly(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Mostrar apenas despesas fixas"
                />
              )}
            </Box>
            <TransactionList 
              transactions={filteredTransactions} 
              loading={loading} 
              onDelete={handleDelete}
              onEdit={handleEditTransaction}
              onViewHistory={handleViewHistory}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Modal de Nova Transação */}
      <Dialog
        open={openNewTransaction}
        onClose={() => {
          setOpenNewTransaction(false);
          setEditingTransaction(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingTransaction ? 'Editar Transação' : 'Nova Transação'}
        </DialogTitle>
        <DialogContent dividers>
          <TransactionForm 
            onTransactionAdded={handleTransactionAdded}
            editingTransaction={editingTransaction}
          />
        </DialogContent>
      </Dialog>

      {/* Modal de Histórico */}
      <Dialog
        open={openHistoryDialog}
        onClose={() => setOpenHistoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Histórico de Alterações
          {selectedTransactionHistory?.transaction && (
            <Typography variant="subtitle2" color="text.secondary">
              {selectedTransactionHistory.transaction.description}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedTransactionHistory?.history.length === 0 ? (
            <Typography color="text.secondary" align="center">
              Nenhuma alteração registrada
            </Typography>
          ) : (
            <List>
              {selectedTransactionHistory?.history.map((record) => (
                <ListItem key={record.id} divider>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1">
                        {record.action === 'created' ? 'Criação' : 'Atualização'}
                        {' - '}
                        <Typography component="span" color="text.secondary">
                          {new Date(record.created_at).toLocaleString('pt-BR')}
                        </Typography>
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        {record.action === 'updated' && (
                          Object.keys(record.changed_fields).map(field => (
                            <Typography key={field} variant="body2">
                              <strong>{field}:</strong> {' '}
                              {JSON.stringify(record.previous_values[field])} → {' '}
                              {JSON.stringify(record.new_values[field])}
                            </Typography>
                          ))
                        )}
                        <Typography variant="caption" color="text.secondary">
                          Por: {record.user?.email}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          p: 2, 
          borderTop: '1px solid rgba(0, 0, 0, 0.12)'
        }}>
          <Button onClick={() => setOpenHistoryDialog(false)}>
            Fechar
          </Button>
        </Box>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </Container>
  );
}

export default CashFlow;
